import {
    faCopy,
    faLink,
    faMoneyBillTransfer,
    faUserGroup,
    faWallet,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { Page } from '~/components/Page'
import {
    EmptyBlock,
    ErrorBlock,
    Panel,
    PRIZE_GREEN,
    SectionHeading,
    Skeleton,
    Stat,
} from '~/components/ui'
import {
    AffiliateManagerDashboard,
    OverrideStatement,
    useAffiliateManagerDashboard,
} from '../api/useAffiliateManager'

const usd = (value: string | undefined) => {
    // `$${n}` puts the sign inside the currency for a loss -- "$-28.00". Formatting
    // the magnitude and placing the sign outside gives the "-$28.00" a reader expects.
    const amount = Number(value ?? 0) || 0
    const formatted = Math.abs(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })
    return `${amount < 0 ? '-' : ''}$${formatted}`
}

const percent = (rate: string | undefined) => `${Math.round((Number(rate ?? 0) || 0) * 100)}%`

const day = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })

/**
 * The link an agency gives its creators.
 *
 * Points at the affiliate pitch page, not at signup — this one is aimed at people
 * who will *apply*, not at players who will play. An agency that also refers
 * players directly has a separate player link on the affiliate dashboard, and
 * keeping the two apart is the whole reason this page exists.
 */
const RecruitingLink = ({ url }: { url: string }) => {
    const [copied, setCopied] = useState(false)

    return (
        <Panel>
            <div className='flex flex-col gap-3 p-4 md:p-5'>
                <span className='text-2xs font-bold uppercase tracking-[0.2em] text-white/40'>
                    Your recruiting link
                </span>
                <button
                    onClick={() => {
                        navigator.clipboard.writeText(url)
                        setCopied(true)
                        setTimeout(() => setCopied(false), 800)
                    }}
                    title='Copy recruiting link'
                    className='group flex min-h-[2.75rem] w-full flex-row items-center justify-between gap-3 border border-white/15 bg-black/40 px-3 py-2 transition-colors hover:border-white/40'
                >
                    <span className='min-w-0 flex-1 overflow-x-auto no-scrollbar whitespace-nowrap text-left text-xs md:text-sm'>
                        {url}
                    </span>
                    <span
                        className={twMerge(
                            'flex shrink-0 flex-row items-center gap-2 text-2xs font-black uppercase tracking-[0.2em]',
                            copied ? 'text-[#00FF90]' : 'text-white/45 group-hover:text-white'
                        )}
                    >
                        <FontAwesomeIcon icon={faCopy} />
                        {copied ? 'Copied' : 'Copy'}
                    </span>
                </button>
                <p className='text-xs font-medium leading-relaxed text-white/45'>
                    Send this to your creators. Anyone who applies through it arrives attached to you, and
                    appears below once we approve them.
                </p>
            </div>
        </Panel>
    )
}

const OverridePanel = ({
    title,
    hint,
    statement,
}: {
    title: string
    hint?: string
    statement: OverrideStatement
}) => (
    <section className='flex flex-col gap-3 md:gap-4'>
        <SectionHeading icon={faWallet} title={title} hint={hint} />
        <Panel>
            <div className='flex flex-col gap-5 p-4 md:p-6'>
                <div className='flex flex-col gap-1'>
                    <span className='text-2xs uppercase tracking-[0.2em] text-white/40'>
                        Your share ({percent(statement.rate)} of your creators' profit)
                    </span>
                    <span
                        className='font-monument text-3xl font-bold leading-none tabular-nums md:text-4xl'
                        style={{ color: PRIZE_GREEN }}
                    >
                        {usd(statement.share)}
                    </span>
                </div>

                <div className='grid grid-cols-2 gap-4 border-t border-white/10 pt-4'>
                    <Stat label='Creators' value={statement.creators} valueClassName='text-lg' />
                    <Stat
                        label='Profit they generated'
                        value={usd(statement.profit)}
                        valueClassName={twMerge('text-lg', statement.is_negative_period && 'text-white/50')}
                    />
                </div>

                {statement.is_negative_period && (
                    <p className='text-xs font-medium leading-relaxed text-white/45'>
                        Your creators' players won more than they spent this period, so there is nothing to
                        pay out yet. Nothing is owed by you — it nets against your next period.
                    </p>
                )}

                {statement.capped && (
                    <p className='text-xs font-medium leading-relaxed text-white/45'>
                        Capped at BR1 take. Your share is never more than BR1 actually kept from your
                        creators' players, after the winnings it paid to them and to the players who beat them.
                    </p>
                )}
            </div>
        </Panel>
    </section>
)

const Roster = ({ creators }: { creators: AffiliateManagerDashboard['creators'] }) => (
    <section className='flex flex-col gap-3 md:gap-4'>
        <SectionHeading icon={faUserGroup} title='Your creators' hint={`${creators.length} total`} />
        {creators.length === 0 ? (
            <EmptyBlock title='No creators yet'>
                Share your recruiting link. Anyone who applies through it and gets approved shows up here.
            </EmptyBlock>
        ) : (
            <Panel>
                <div className='overflow-x-auto'>
                    <table className='w-full text-sm'>
                        <thead>
                            <tr className='border-b border-white/10 text-2xs uppercase tracking-[0.15em] text-white/40'>
                                <th className='p-3 text-left font-bold md:p-4'>Creator</th>
                                <th className='p-3 text-left font-bold md:p-4'>Joined</th>
                                <th className='p-3 text-right font-bold md:p-4'>Signups</th>
                                <th className='p-3 text-right font-bold md:p-4'>Profit</th>
                                <th className='p-3 text-right font-bold md:p-4'>They earn</th>
                            </tr>
                        </thead>
                        <tbody>
                            {creators.map((creator) => (
                                <tr key={creator.username} className='border-b border-white/5 last:border-0'>
                                    {/* Stored case — uppercasing a username is wrong. */}
                                    <td className='p-3 font-medium md:p-4'>{creator.username}</td>
                                    <td className='p-3 text-white/50 md:p-4'>{day(creator.joined)}</td>
                                    <td className='p-3 text-right tabular-nums md:p-4'>{creator.signups}</td>
                                    <td className='p-3 text-right tabular-nums md:p-4'>{usd(creator.profit)}</td>
                                    <td className='p-3 text-right tabular-nums text-white/60 md:p-4'>
                                        {usd(creator.creator_share)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Panel>
        )}
    </section>
)

const Payouts = ({ rows }: { rows: AffiliateManagerDashboard['payouts'] }) => (
    <section className='flex flex-col gap-3 md:gap-4'>
        <SectionHeading icon={faMoneyBillTransfer} title='Payouts' />
        {rows.length === 0 ? (
            <EmptyBlock title='No payments yet'>
                Statements close monthly and are reviewed by a person before payment. Every payment we make
                will be listed here.
            </EmptyBlock>
        ) : (
            <Panel>
                <div className='overflow-x-auto'>
                    <table className='w-full text-sm'>
                        <thead>
                            <tr className='border-b border-white/10 text-2xs uppercase tracking-[0.15em] text-white/40'>
                                <th className='p-3 text-left font-bold md:p-4'>Period</th>
                                <th className='p-3 text-left font-bold md:p-4'>Paid</th>
                                <th className='p-3 text-right font-bold md:p-4'>Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr
                                    key={`${row.period_start}-${row.paid_at}`}
                                    className='border-b border-white/5 last:border-0'
                                >
                                    <td className='p-3 md:p-4'>
                                        {day(row.period_start)} – {day(row.period_end)}
                                    </td>
                                    <td className='p-3 text-white/50 md:p-4'>{day(row.paid_at)}</td>
                                    <td
                                        className='p-3 text-right font-black tabular-nums md:p-4'
                                        style={{ color: PRIZE_GREEN }}
                                    >
                                        {usd(row.amount)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Panel>
        )}
    </section>
)

/**
 * The agency's home.
 *
 * A reduced version of what staff see: this agency's own creators and nothing
 * else. No other agencies, no email addresses, and no way to record a payment —
 * BR1 pays, so BR1 records it.
 *
 * The two money figures stay apart on purpose. The override is what the agency
 * earns on its creators; "your own referrals" is what it earns from players it
 * brought in directly. Adding them together would leave an agency unable to tell
 * whether recruiting or promoting is the thing that is working.
 */
export const AffiliateManagerToolsPage = () => {
    const { data, isLoading, isError, refetch } = useAffiliateManagerDashboard()
    const ownLifetime = Number(data?.own.lifetime.profit ?? 0)

    return (
        <Page title='Affiliate Manager Tools' header='Manager Tools'>
            <div className='mx-auto flex w-full max-w-[1100px] flex-col gap-10 px-4 py-6 md:gap-14 md:px-8 md:py-10'>
                <h1 className='sr-only'>Affiliate Manager Tools</h1>

                {isError ? (
                    <ErrorBlock onRetry={() => refetch()}>Your manager dashboard could not be loaded.</ErrorBlock>
                ) : isLoading || !data ? (
                    <div className='flex flex-col gap-4'>
                        <Skeleton className='h-28 w-full' />
                        <Skeleton className='h-24 w-full' />
                        <Skeleton className='h-64 w-full' />
                    </div>
                ) : (
                    <>
                        <section className='flex flex-col gap-3 md:gap-4'>
                            <SectionHeading icon={faLink} title='Recruiting' />
                            <RecruitingLink url={data.recruiting_url} />
                        </section>

                        <OverridePanel title='This month' hint='So far' statement={data.override.current_period} />
                        <OverridePanel title='Lifetime' statement={data.override.lifetime} />
                        <Roster creators={data.creators} />

                        {/*
                          Only shown once it is a real number. An agency that has
                          never used its own player link does not need a permanent
                          row of zeros explaining a thing it does not do.
                        */}
                        {ownLifetime !== 0 && (
                            <section className='flex flex-col gap-3 md:gap-4'>
                                <SectionHeading
                                    icon={faWallet}
                                    title='Your own referrals'
                                    hint='Players you brought in directly'
                                />
                                <Panel>
                                    <div className='grid grid-cols-2 gap-4 p-4 md:grid-cols-4 md:p-6'>
                                        <Stat
                                            label='This month'
                                            value={usd(data.own.current_period.share)}
                                            valueClassName='text-lg'
                                        />
                                        <Stat
                                            label='Lifetime'
                                            value={usd(data.own.lifetime.share)}
                                            valueClassName='text-lg'
                                        />
                                        <Stat label='Signups' value={data.funnel.signups} valueClassName='text-lg' />
                                        <Stat
                                            label='Still playing'
                                            value={data.funnel.active_players}
                                            valueClassName='text-lg'
                                        />
                                    </div>
                                </Panel>
                            </section>
                        )}

                        <Payouts rows={data.payouts} />
                    </>
                )}
            </div>
        </Page>
    )
}
